import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/authStore";
import { authApi } from "@/api/authApi";
import type { LoginCredentials, RegisterData } from "@/types/auth";
import { navigate } from "wouter/use-browser-location";
import { useEffect } from "react";

export const useAuth = () => {
	const queryClient = useQueryClient();
	const { setAccessToken } = useAuthStore();

	const resetPasswordMutation = useMutation({
		mutationFn: ({ token, password }: { token: string; password: string }) =>
		  authApi.resetPassword(token, password),
		onSuccess: () => {
		  navigate("/login", { replace: true });
		},
		onError: (error) => {
		  console.error("Reset Password Error:", error);
		},
	  });

	  const forgotPasswordMutation = useMutation({
		mutationFn: (email: string) => authApi.forgotPassword(email),
		onSuccess: () => {
		},
		onError: (error: any) => {
		  console.error("Forgot Password Error:", error?.response?.data?.message || error);
		},
	  });
	  

	const registerMutation = useMutation({
		mutationFn: (registerData: RegisterData) => authApi.register(registerData)
	});

	const loginMutation = useMutation({
		mutationFn: async (credentials: LoginCredentials) => {
			const response = await authApi.login(credentials);
			return response; 
		  },
	});

	const logoutMutation = useMutation({
		mutationFn: authApi.logout,
		onSuccess: () => {
			queryClient.clear();
		},
	});

	const refreshQuery = useQuery({
		queryKey: ["refresh"],
		queryFn: () => authApi.refreshToken(),
		retry: 1,
		staleTime: 15 * 60 * 1000,
		gcTime: 0,
	});

	useEffect(() => {
		if (refreshQuery.data) {
			setAccessToken(refreshQuery.data.accessToken);
		}
	}, [refreshQuery.data, setAccessToken]);

	const userQuery = useQuery({
		queryKey: ["user"],
		queryFn: () => authApi.getUser(),
		select: (data) => {
			return data;
		},
		enabled: !!useAuthStore.getState().accessToken,
	});

	return {
		forgotPassword: forgotPasswordMutation.mutate,
		isForgotPasswordLoading: forgotPasswordMutation.isPending,
		resetPassword: resetPasswordMutation.mutate,
		isResetPasswordLoading: resetPasswordMutation.isPending,
		register: registerMutation.mutate,
		isRegistering: registerMutation.isPending,
		login: loginMutation.mutate,
		logout: logoutMutation,
		isAuthenticated:
			!!refreshQuery.data?.accessToken || !!useAuthStore.getState().accessToken,
		isLoading:
			loginMutation.isPending ||
			logoutMutation.isPending ||
			userQuery.isLoading,
		user: userQuery.data,
		isLoadingUserInfo: userQuery.isLoading
	};
};
